import React, { useState, useRef, useEffect, useMemo } from "react";
import { Input as SInput } from "semantic-ui-react";
import PropTypes from "prop-types";
import {connectForm, Field, FormUtils, FormValidators } from "@redriver/cinnamon";

const FormattedNumericInput = ({
                                   decimalPlaces = 0,
                                   selectOnFocus = true,
                                   placeholder = "",
                                   className = "",
                                   passThruProps,
                                   fluid,
                                   onChange,
                                   customValidator,
                                   canBeNull = false,
                                   required = false,
                                   disabled,
                                   renderReadOnly,
                                   readOnly,
                                   label,
                                   subLabel,
                                   subLabelPosition,
                                   showSubLabelInReadOnly = false,
                                   minValue = null,
                                   maxValue = null,
                                   width = 20,
                                   highlighted = false,
                                   inline = false,
                                   customErrors = [],
                                   showErrors,
                                   allErrors,
                                   errors,
                                   animateErrors,
                                   value,
                                   onBlur: onBlurProp,
                                   ...props
                               }) => {
    const [formatValue, setFormatValue] = useState(value);
    const [focused, setFocused] = useState(false);
    const inputRef = useRef(null);
    const [lastValidValue, setLastValidValue] = useState(formatValue);

    const maxDecimalPlaces = useMemo(() => decimalPlaces?.max ?? decimalPlaces, [decimalPlaces]);
    const minDecimalPlaces = useMemo(() => decimalPlaces?.min ?? decimalPlaces, [decimalPlaces]);

    // Update internal state if initialValue changes
    useEffect(() => {
        setFormatValue(value);
        setLastValidValue(value);
    }, [value]);

    // useEffect to run custom validator when value changes
    useEffect(() => {
        if (customValidator && !focused) {
            customValidator();
        }
    }, [value, customValidator, focused]);

    // Format the number with commas and fixed decimal places
    const formatNumber = (val) => {
        if (val === "" || val === null || val === undefined || isNaN(val)) return val; // Safety default e.g. if value is not a number return original val without formatting
        const num = parseFloat(val);
        return num.toLocaleString(undefined, {
            minimumFractionDigits: minDecimalPlaces,
            maximumFractionDigits: maxDecimalPlaces,
        });
    };

    // Remove commas to get raw numeric value
    const stripFormatting = (val) => {
        if (typeof val !== "string") return val;
        return val.replace(/,/g, "");
    };

    // Normalize decimal numbers to always have a leading zero
    const normalizeDecimal = (val) => {
        if (typeof val !== 'string') return val;

        // Add leading zero to values that start with decimal point
        if (val.startsWith('.')) {
            return `0${val}`;
        }
        return val;
    };

    // Check if value is within min/max bounds
    const isWithinBounds = (val) => {
        if (val === null || val === undefined || val === '') return true;

        const numValue = parseFloat(val);

        if (isNaN(numValue)) return false;

        if (minValue !== null && numValue < minValue) return false;
        if (maxValue !== null && numValue > maxValue) return false;

        return true;
    };

    // Constrain value to min/max bounds
    const constrainToBounds = (val) => {
        if (val === null || val === undefined || val === '' || val === '-' || val === '.') return val;

        let numValue = parseFloat(val);

        if (isNaN(numValue)) return lastValidValue;

        if (minValue !== null && numValue < minValue) numValue = minValue;
        if (maxValue !== null && numValue > maxValue) numValue = maxValue;

        return numValue.toString();
    };

    const handleFocus = (e) => {
        setFocused(true);
        if (selectOnFocus) {
            e.target.select();
        }
    };

    const handleBlur = () => {
        setFocused(false);

        // Only process if formatValue is not empty, null, or undefined
        if (formatValue !== "" && formatValue !== null && formatValue !== undefined) {
            const rawValue = stripFormatting(formatValue);
            const rawStr = String(rawValue);

            if (rawStr === '-') {
                updateValue(null);
            } else if (rawStr.endsWith('.')) {
                const fixedValue = rawStr.replace(/\.$/, '');
                let constrained = constrainToBounds(fixedValue);
                if (decimalPlaces === 0 && !isNaN(constrained)) {
                    constrained = Math.round(parseFloat(constrained)).toString();
                }
                updateValue(constrained);
            } else if (rawStr.startsWith('.')) {
                const normalized = normalizeDecimal(rawStr);
                let constrained = constrainToBounds(normalized);
                if (decimalPlaces === 0 && !isNaN(constrained)) {
                    constrained = Math.round(parseFloat(constrained)).toString();
                }
                updateValue(constrained);
            } else {
                let constrained = constrainToBounds(rawStr);
                if (decimalPlaces === 0 && !isNaN(constrained)) {
                    constrained = Math.round(parseFloat(constrained)).toString();
                }
                updateValue(constrained);
            }
        } else {
            updateValue(canBeNull ? null : 0);
        }
    };



    // Update both internal state and notify parent
    const updateValue = (newValue) => {
        setFormatValue(newValue);

        // Call the form's onChange handler
        if (onChange) {
            onChange(newValue);
        }

        if (onBlurProp) {
            onBlurProp();
        }
    };

    const handleChange = (e) => {
        let rawValue = stripFormatting(e.target.value);

        // Handle empty input case
        if (rawValue === "") {
            updateValue(null);
            return;
        }

        // Allow a lone minus sign for starting negative number input
        // (but only if min allows negative values)
        if (rawValue === '-') {
            if (minValue === null || minValue < 0) {
                updateValue(rawValue);
            }
            return;
        }

        // Allow a lone decimal point (will be converted to "0." later)
        if (rawValue === '.') {
            updateValue(rawValue);
            return;
        }

        // Prevent multiple decimal points
        if ((rawValue.match(/\./g) || []).length > 1) {
            rawValue = rawValue.replace(/\.+$/, "");
        }

        rawValue = limitDecimalPlacesOnInput(rawValue);

        // Check if input is valid (including negative numbers)
        const isValidInput = rawValue === "" ||
            !isNaN(rawValue) ||
            (rawValue.endsWith('.') && !isNaN(rawValue.slice(0, -1)));

        if (isValidInput) {
            // While typing, allow values outside bounds

            // Only update lastValidValue if this is a complete valid number
            if (!rawValue.endsWith('.') && isWithinBounds(rawValue)) {
                // Store normalized value in lastValidValue
                setLastValidValue(rawValue.startsWith('.') ? normalizeDecimal(rawValue) : rawValue);
            }

            // Update the state with the formatted value
            updateValue(rawValue);
        } else {
            // If invalid, revert to the last valid value
            updateValue(lastValidValue);
        }
    };

    const limitDecimalPlacesOnInput = (input) => {
        // Regular expression to match the number with limited decimal places
        const regex = new RegExp(`^\\d*(\\.\\d{0,${maxDecimalPlaces}})?$`);
        
        // Check if the input matches the regex
        if (regex.test(input)) {
            return input; // Valid input
        } else {
            // If invalid, return the last valid input
            return input.slice(0, -1); // Remove the last character
        }
    }

    // Handle keydown to restrict input to only numeric values, a single decimal point, and minus sign
    const handleKeyDown = (e) => {
        const key = e.key;
        const currentValue = stripFormatting(e.target.value);
        const selectionStart = e.target.selectionStart;

        // Allow keyboard shortcuts (Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X)
        if ((e.ctrlKey || e.metaKey) && (
            key === 'a' ||
            key === 'c' ||
            key === 'v' ||
            key === 'x' ||
            key === 'z'
        )) {
            return;
        }

        // Allow keys for editing
        if (
            key === "Backspace" ||
            key === "Delete" ||
            key === "ArrowLeft" ||
            key === "ArrowRight" ||
            key === "ArrowUp" ||
            key === "ArrowDown" ||
            key === "Tab" ||
            key === "Home" ||
            key === "End"
        ) {
            return;
        }

        // Allow numeric input (0-9), decimal point, or minus sign
        if (!/[0-9.\-]/.test(key)) {
            e.preventDefault();
            return;
        }

        // Prevent second decimal point
        if (key === '.' && currentValue.includes('.')) {
            e.preventDefault();
            return;
        }

        // Handle minus sign - only allow at the beginning, and only if min allows negative values
        if (key === '-') {
            if (selectionStart > 0 || currentValue.includes('-') || (minValue !== null && minValue >= 0)) {
                e.preventDefault();
                return;
            }
        }
    };

    // Handle paste events to validate
    const handlePaste = (e) => {
        const clipboardData = e.clipboardData || window.clipboardData;
        const pastedData = clipboardData.getData('Text');

        // Clean the pasted data
        const cleanedData = pastedData.trim();

        // Accept only if it's a valid number
        const isValidNumber = /^-?\d*\.?\d*$/.test(cleanedData) && !isNaN(parseFloat(cleanedData));

        // Check if negative numbers are allowed
        const allowNegative = minValue === null || minValue < 0;
        if (!allowNegative && cleanedData.startsWith('-')) {
            e.preventDefault();
            return;
        }

        if (!isValidNumber && cleanedData !== '-' && cleanedData !== '.') {
            e.preventDefault();
        }
    };

    const handleInput = (e) => {
        const rawValue = stripFormatting(e.target.value);

        // Prevent more than one decimal point
        if ((rawValue.match(/\./g) || []).length > 1) {
            e.preventDefault();
        }

        // Allow lone minus or decimal
        if (rawValue === '-' || rawValue === '.') {
            return;
        }

        // Check if it's a valid number with possible decimal or minus
        if (rawValue && !rawValue.endsWith('.') && isNaN(rawValue)) {
            e.preventDefault();
        }
    };

    // For display, use the raw value when focused
    let displayValue;
    if (focused) {
        displayValue = stripFormatting(formatValue);
    } else {
        // Normalize value before formatting
        const normalizedValue = formatValue !== null && formatValue !== undefined &&
        typeof formatValue === 'string' && formatValue.startsWith('.')
            ? normalizeDecimal(formatValue)
            : formatValue;
        displayValue = formatNumber(normalizedValue);
    }

    // Method to get current value (can be exposed to parent components)
    const getValue = () => {
        return formatValue;
    };

    const setValueManually = (newValue) => {
        let constrained = constrainToBounds(newValue);
        if (decimalPlaces === 0 && !isNaN(constrained)) {
            constrained = Math.round(parseFloat(constrained)).toString();
        }
        updateValue(constrained);
    };


    const defaultRenderReadOnly = () => <p>{formatNumber(value)}</p>;

    const readOnlyWithSubLabel = () => {
        const formatted = formatNumber(value);
        if (formatted === null || formatted === undefined || formatted === "") return formatted;

        return subLabelPosition === "right"
            ? `${formatted}${subLabel}`
            : `${subLabel}${formatted}`;
    };

    renderReadOnly = () =>
        (showSubLabelInReadOnly && subLabel
            ? readOnlyWithSubLabel()
            : formatNumber(value)) || defaultRenderReadOnly;

    // Expose methods to parent through the ref
    React.useImperativeHandle(passThruProps.forwardedRef, () => ({
        getValue,
        setValue: setValueManually,
        inputRef: inputRef.current
    }));

    return (
        <Field
            required={required}
            disabled={disabled}
            renderReadOnly={readOnly && renderReadOnly}
            errors={FormUtils.fieldErrors(errors, showErrors, allErrors)}
            animateErrors={animateErrors}
            label={label}
            width={width}
            inline={inline}
        >
            <SInput
                className={`${highlighted && "highlighted-input"} formatted-numeric-input ${className}`}
                ref={inputRef}
                value={displayValue}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                onInput={handleInput}
                onPaste={handlePaste}
                placeholder={placeholder}
                label={subLabel}
                labelPosition={subLabelPosition}
                fluid={fluid}
            />
        </Field>
    );
};

FormattedNumericInput.propTypes = {
    initialValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func,
    customValidator: PropTypes.func,
    decimalPlaces: PropTypes.oneOfType([PropTypes.number, PropTypes.shape({
        min: PropTypes.number,
        max: PropTypes.number
    })]),
    selectOnFocus: PropTypes.bool,
    forwardedRef: PropTypes.object,
    minValue: PropTypes.number,
    maxValue: PropTypes.number,
    value: PropTypes.number,
    renderReadOnly: PropTypes.bool,
    readOnly: PropTypes.bool,
    showSubLabelInReadOnly: PropTypes.bool,
    customErrors: PropTypes.arrayOf(PropTypes.string),
    showErrors: PropTypes.bool,
    errors: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.string),
        PropTypes.object,
    ]),
    animateErrors: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
};

const FormattedNumericInputWithRef = React.forwardRef((props, ref) => (
    <FormattedNumericInput {...props} forwardedRef={ref} />
));

export default connectForm({
    displayName: (props) =>
    props.label && typeof props.label === "string"
      ? props.label
      : FormUtils.prettifyField(props.field),
  validators: [
    FormValidators.requiredField(false),
    FormValidators.number(true),
    FormValidators.minValue(),
    FormValidators.maxValue(),
  ]
})(FormattedNumericInputWithRef);